/**
 * GM Workwear Custom Order Builder — Pricing Engine & Cart Integration
 * ------------------------------------------------------------------------
 */

(function () {
  'use strict';

  const config = window.GM_CUSTOMIZATION || {};
  if (!config.productId) return;

  const S = config.strings || {};
  const WORKER_URL = config.uploadWorkerUrl || 'https://upload-to-shopify-worker-gmworkwear.noumanakbardeveloper.workers.dev';

  /* ------------------------------------------------------------------
     Variant Lookup — All needed variants loaded in Liquid by iterating
     types and positions, avoiding the 250 variant limit
     ------------------------------------------------------------------ */
  console.log('[init] Customization variants loaded:', config.customizationProduct?.variants?.length || 0);


  /* ------------------------------------------------------------------
     State
     ------------------------------------------------------------------ */
  const state = {
    groups: [],           // finalized color groups
    current: null,        // group being built
    editingIndex: -1,     // index in groups being edited, or -1 for new
    files: new Map(),     // positionKey -> base64 data URL string (uploaded on Add to Cart)
  };

  /* ------------------------------------------------------------------
     DOM refs (lazy)
     ------------------------------------------------------------------ */
  let els = null;
  function getEls() {
    if (els) return els;
    const root = document.querySelector('[data-gm-cob]');
    if (!root) return null;
    els = {
      root,
      selectedColors: root.querySelector('.gm-cob__selected-colors'),
      colorBtns: root.querySelectorAll('.gm-cob__color-swatch'),
      sizeInputs: root.querySelectorAll('.gm-cob__qty-input'),
      matrixHeader: root.querySelector('.gm-cob__matrix-header'),
      addColorBtn: root.querySelector('.gm-cob__add-color-btn'),
      typeBtns: root.querySelectorAll('.gm-cob__type-btn'),
      positionsWrap: root.querySelector('.gm-cob__positions-wrap'),
      positionCards: () => root.querySelectorAll('.gm-cob__position-card'),
      positionsSummary: root.querySelector('.gm-cob__positions-summary'),
      uploadsWrap: root.querySelector('.gm-cob__uploads-wrap'),
      uploadZones: () => root.querySelectorAll('.gm-cob__upload-zone'),
      breaksRows: root.querySelectorAll('.gm-cob__breaks-row'),
      cartBtn: root.querySelector('.gm-cob__cart-btn'),
      errors: root.querySelector('.gm-cob__errors'),
      cartBtnText: root.querySelector('.gm-cob__cart-btn-text'),
      overlay: root.querySelector('[data-gm-overlay]'),
      overlayProcessing: root.querySelector('[data-gm-overlay-processing]'),
      overlayResult: root.querySelector('[data-gm-overlay-result]'),
      overlayStep: root.querySelector('[data-gm-overlay-step]'),
      overlayStatus: root.querySelector('[data-gm-overlay-status]'),
      overlayBar: root.querySelector('[data-gm-overlay-bar]'),
      overlayDetail: root.querySelector('[data-gm-overlay-detail]'),
      overlaySuccessIcon: root.querySelector('[data-gm-overlay-success-icon]'),
      overlayErrorIcon: root.querySelector('[data-gm-overlay-error-icon]'),
      overlayTitle: root.querySelector('[data-gm-overlay-title]'),
      overlayContext: root.querySelector('[data-gm-overlay-context]'),
      overlaySub: root.querySelector('[data-gm-overlay-sub]'),
      overlayActions: root.querySelector('[data-gm-overlay-actions]'),
    };
    // Reparent overlay to <body> so it escapes any ancestor transforms
    // (e.g. scroll-trigger animations that create a containing block)
    if (els.overlay && els.overlay.parentElement && els.overlay.parentElement !== document.body) {
      document.body.appendChild(els.overlay);
    }
    return els;
  }

  /* ------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------ */
  const fmtMoney = (cents) => {
    const sym = config.currencySymbol || '£';
    return sym + (cents / 100).toFixed(2);
  };

  const findVariant = (colorName, sizeName) => {
    const variants = config.variants || [];
    // 1. Exact match: variant has both color and size in its options
    let match = variants.find(v => {
      const opts = [v.option1, v.option2, v.option3];
      return opts.includes(colorName) && opts.includes(sizeName);
    });
    if (match) return match;
    // 2. Match by color only
    match = variants.find(v => [v.option1, v.option2, v.option3].includes(colorName));
    if (match) return match;
    // 3. Match by size only
    match = variants.find(v => [v.option1, v.option2, v.option3].includes(sizeName));
    if (match) return match;
    // 4. Fallback to first available variant so the item is never dropped
    return variants[0] || null;
  };

  const normalizePosition = (raw) => {
    if (!raw) return '';
    let s = String(raw).trim().toLowerCase();
    // Fix common misspellings
    s = s.replace(/seleeve/g, 'sleeve');
    // Map short names to full names
    s = s.replace(/^front$/, 'full front');
    s = s.replace(/^back$/, 'full back');
    s = s.replace(/^left leg$/, 'left upper leg');
    s = s.replace(/^right leg$/, 'right upper leg');
    return s;
  };

  const findCustomizationVariant = (typeName, positionName) => {
    const variants = config.customizationProduct?.variants || [];
    if (!typeName || !positionName) return null;

    // Normalize position if it comes as an object from metafields
    const rawPos = typeof positionName === 'object' && positionName !== null ? (positionName.position || positionName.name || '') : positionName;
    const t = String(typeName).trim().toLowerCase();
    const p = normalizePosition(rawPos);

    const searchKey = `${t}|${p}`;
    const debugInfo = {
      search: searchKey,
      totalVariants: variants.length,
      sampleVariants: variants.slice(0, 3).map(v => ({ option1: v.option1, option2: v.option2, price: v.price }))
    };

    // --- Pass 1: exact match (option1=type, option2=position) ---
    let match = variants.find(v => {
      const o1 = String(v.option1 || '').trim().toLowerCase();
      const o2 = normalizePosition(v.option2 || '');
      return o1 === t && o2 === p;
    });
    if (match) {
      console.log('[findCustomizationVariant] Pass 1 (exact) match:', { search: searchKey, match: { option1: match.option1, option2: match.option2, price: match.price } });
      return match;
    }

    // --- Pass 2: reversed match (option1=position, option2=type) ---
    match = variants.find(v => {
      const o1 = normalizePosition(v.option1 || '');
      const o2 = String(v.option2 || '').trim().toLowerCase();
      return o1 === p && o2 === t;
    });
    if (match) {
      console.log('[findCustomizationVariant] Pass 2 (reversed) match:', { search: searchKey, match: { option1: match.option1, option2: match.option2, price: match.price } });
      return match;
    }

    // --- Pass 3: title segment match ---
    match = variants.find(v => {
      const title = normalizePosition(v.title || '');
      return title.includes(t) && title.includes(p);
    });
    if (match) {
      console.log('[findCustomizationVariant] Pass 3 (title) match:', { search: searchKey, match: { title: match.title, price: match.price } });
      return match;
    }

    // --- Pass 4: partial option match (e.g. metafield "Front" → variant "Full Front") ---
    match = variants.find(v => {
      const o1 = String(v.option1 || '').trim().toLowerCase();
      const o2 = normalizePosition(v.option2 || '');
      if (o1 !== t) return false;
      // option2 contains position OR position contains option2
      return o2.includes(p) || p.includes(o2);
    });
    if (match) {
      console.log('[findCustomizationVariant] Pass 4 (partial) match:', { search: searchKey, match: { option1: match.option1, option2: match.option2, price: match.price } });
      return match;
    }

    // --- Pass 5: title partial match (type exact, position partial) ---
    match = variants.find(v => {
      const title = normalizePosition(v.title || '');
      const opt2 = normalizePosition(v.option2 || '');
      if (!title.includes(t)) return false;
      // title contains position OR position is contained in option2
      return title.includes(p) || p.includes(opt2) || opt2.includes(p);
    });
    if (match) {
      console.log('[findCustomizationVariant] Pass 5 (title partial) match:', { search: searchKey, match: { title: match.title, price: match.price } });
      return match;
    }

    console.warn('[findCustomizationVariant] NO MATCH FOUND:', debugInfo);
    return null;
  };

  const findCombinedCustomizationVariant = (typeName, positionNames) => {
    const variants = config.customizationProduct?.variants || [];
    if (!typeName || !positionNames || positionNames.length <= 1) return null;

    const t = String(typeName).trim().toLowerCase();
    const positions = positionNames
      .map(p => normalizePosition(p.position || p))
      .filter(Boolean)
      .sort();

    if (positions.length === 0) return null;

    // --- Pass 1: option2 contains every position name ---
    let match = variants.find(v => {
      const o1 = String(v.option1 || '').trim().toLowerCase();
      if (o1 !== t) return false;
      const o2 = normalizePosition(v.option2 || '');
      return positions.every(p => o2.includes(p));
    });
    if (match) return match;

    // --- Pass 2: title contains type and every position name ---
    match = variants.find(v => {
      const title = normalizePosition(v.title || '');
      if (!title.includes(t)) return false;
      return positions.every(p => title.includes(p));
    });
    if (match) return match;

    return null;
  };

  const getTotalQty = () => {
    let total = 0;
    state.groups.forEach(g => {
      Object.values(g.sizes || {}).forEach(q => total += (parseInt(q, 10) || 0));
    });
    if (state.current) {
      Object.values(state.current.sizes || {}).forEach(q => total += (parseInt(q, 10) || 0));
    }
    return total;
  };

  const getDiscountTier = (qty) => {
    const tiers = (config.discountTiers || []).slice().sort((a, b) => a.min_quantity - b.min_quantity);
    let matched = null;
    for (const t of tiers) {
      const max = t.max_quantity != null ? t.max_quantity : Infinity;
      if (qty >= t.min_quantity && qty <= max) matched = t;
    }
    return matched;
  };

  const getPositionPrice = (type, position) => {
    if (!type || !position) {
      console.warn('[getPositionPrice] Missing type or position:', { type, position });
      return 0;
    }
    const variant = findCustomizationVariant(type, position);
    if (!variant) {
      console.warn('[getPositionPrice] No variant found for:', { type, position, availableVariants: (config.customizationProduct?.variants || []).length });
      return 0;
    }
    return variant.price || 0;
  };

  const getCustomizationPerUnit = () => {
    const type = state.current?.type || '';
    if (!type) return 0;
    const positions = state.current?.positions || [];
    if (positions.length === 0) return 0;

    // In multiple mode with multiple positions, try to find a single combined variant first
    const multiMode = config.allowMultiplePositions === 'multiple';
    if (multiMode && positions.length > 1) {
      const combined = findCombinedCustomizationVariant(type, positions);
      if (combined) return combined.price || 0;
    }

    return positions.reduce((sum, p) => sum + getPositionPrice(type, p.position), 0);
  };

  const groupTotalQty = (group) => {
    return Object.values(group.sizes || {}).reduce((s, q) => s + (parseInt(q, 10) || 0), 0);
  };

  /* ------------------------------------------------------------------
     Pricing
     ------------------------------------------------------------------ */
  function calculateTotals() {
    const totalQty = getTotalQty();
    const tier = getDiscountTier(totalQty);
    const discountPct = tier ? (tier.discount_percentage || 0) : 0;
    const discountMul = 1 - discountPct / 100;
    const cusPerUnit = getCustomizationPerUnit();

    let lineBaseTotal = 0;

    // Current group
    if (state.current && state.current.color) {
      for (const [size, qtyStr] of Object.entries(state.current.sizes || {})) {
        const qty = parseInt(qtyStr, 10) || 0;
        if (qty <= 0) continue;
        const variant = findVariant(state.current.color, size);
        const base = variant ? (variant.price || 0) : 0;
        lineBaseTotal += (base * discountMul + cusPerUnit) * qty;
      }
    }

    // Finalized groups
    state.groups.forEach(g => {
      for (const [size, qtyStr] of Object.entries(g.sizes || {})) {
        const qty = parseInt(qtyStr, 10) || 0;
        if (qty <= 0) continue;
        const variant = findVariant(g.color, size);
        const base = variant ? (variant.price || 0) : 0;
        lineBaseTotal += (base * discountMul + cusPerUnit) * qty;
      }
    });

    return { totalQty, discountPct, tier, grandTotal: lineBaseTotal };
  }

  function updatePricingDisplay() {
    const e = getEls();
    if (!e) return;
    const { totalQty, discountPct, tier } = calculateTotals();

    // Highlight active discount tier
    e.breaksRows.forEach(row => {
      const min = parseInt(row.dataset.min, 10);
      const max = row.dataset.max ? parseInt(row.dataset.max, 10) : Infinity;
      row.classList.toggle('active', totalQty >= min && totalQty <= max);
    });

    // Update base price display on each tier row
    e.breaksRows.forEach(row => {
      const priceEl = row.querySelector('.gm-cob__breaks-price');
      if (!priceEl || !config.baseVariantPrice) return;
      const rowPct = parseFloat(row.dataset.discount) || 0;
      const discounted = config.baseVariantPrice * (1 - rowPct / 100);
      priceEl.textContent = fmtMoney(discounted);
    });

    // Update cart button total
    const { grandTotal } = calculateTotals();
    if (e.cartBtnText) {
      const tpl = S.cartButtonTemplate || 'Add to Cart \u2014 {price}';
      e.cartBtnText.textContent = tpl.replace('{price}', fmtMoney(grandTotal));
    }
  }

  /* ------------------------------------------------------------------
     Group Management
     ------------------------------------------------------------------ */
  function resetCurrentGroup() {
    const savedType = state.current?.type || '';
    const savedPositions = state.current?.positions ? [...state.current.positions] : [];
    state.current = {
      color: '',
      sizes: {},
      type: savedType,
      positions: savedPositions,
    };
    state.files.clear();
    state.editingIndex = -1;
  }

  function renderSelectedColors() {
    const e = getEls();
    if (!e || !e.selectedColors) return;

    e.selectedColors.innerHTML = '';
    state.groups.forEach((g, idx) => {
      const colorInfo = (config.colors || []).find(c => c.name === g.color) || { name: g.color };
      const total = groupTotalQty(g);
      const div = document.createElement('div');
      div.className = 'gm-cob__color-card gm-cob__anim-in';
      div.innerHTML = `
        <span class="gm-cob__color-card-swatch" style="background:${colorInfo.hex || '#ccc'};${colorInfo.image ? `background-image:url(${colorInfo.image});background-size:cover;` : ''}"></span>
        <div class="gm-cob__color-card-info">
          <div class="gm-cob__color-card-name">${colorInfo.name}</div>
          <div class="gm-cob__color-card-qty">${total} ${S.piecesSuffix || 'pieces'}</div>
        </div>
        <div class="gm-cob__color-card-actions">
          <button type="button" data-edit="${idx}" title="${S.editTitle || 'Edit'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
          </button>
          <button type="button" data-remove="${idx}" title="${S.removeTitle || 'Remove'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
      `;
      e.selectedColors.appendChild(div);
    });

    // Bind edit/remove
    e.selectedColors.querySelectorAll('button[data-edit]').forEach(btn => {
      btn.addEventListener('click', () => editGroup(parseInt(btn.dataset.edit, 10)));
    });
    e.selectedColors.querySelectorAll('button[data-remove]').forEach(btn => {
      btn.addEventListener('click', () => removeGroup(parseInt(btn.dataset.remove, 10)));
    });
  }

  function editGroup(idx) {
    const g = state.groups[idx];
    if (!g) return;
    const globalType = state.current?.type || '';
    const globalPositions = state.current?.positions ? [...state.current.positions] : [];
    state.current = JSON.parse(JSON.stringify(g));
    state.current.type = globalType;
    state.current.positions = globalPositions;
    state.files = new Map(Object.entries(g.files || {}));
    state.editingIndex = idx;
    state.groups.splice(idx, 1);
    renderSelectedColors();
    syncUIFromCurrent();
    updateAddColorButton();
    updatePricingDisplay();
  }

  function removeGroup(idx) {
    state.groups.splice(idx, 1);
    renderSelectedColors();
    updatePricingDisplay();
  }

  function syncUIFromCurrent() {
    const e = getEls();
    if (!e) return;

    // Color
    e.colorBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.color === state.current.color);
    });

    // Selected color label
    const selectedLabel = e.root.querySelector('[data-gm-selected-color]');
    if (selectedLabel) {
      selectedLabel.textContent = state.current.color || 'None selected';
    }

    // Color label in matrix
    const colorInfo = (config.colors || []).find(c => c.name === state.current.color) || {};
    const swatch = e.matrixHeader?.querySelector('.gm-cob__matrix-color-swatch');
    const label = e.matrixHeader?.querySelector('.gm-cob__matrix-color-name');
    if (swatch) {
      swatch.style.background = colorInfo.hex || '#ccc';
      if (colorInfo.image) {
        swatch.style.backgroundImage = `url(${colorInfo.image})`;
        swatch.style.backgroundSize = 'cover';
      } else {
        swatch.style.backgroundImage = '';
      }
    }
    if (label) label.textContent = colorInfo.name || 'Select a color';

    // Sizes
    e.sizeInputs.forEach(inp => {
      inp.value = state.current.sizes[inp.dataset.size] || '0';
      const invEl = inp.closest('.gm-cob__matrix-item')?.querySelector('.gm-cob__matrix-inventory');
      if (invEl && state.current.color) {
        const variant = findVariant(state.current.color, inp.dataset.size);
        if (!variant || variant.available === false) {
          invEl.textContent = 'Out of stock';
          inp.disabled = true;
        } else if (variant.inventory != null) {
          invEl.textContent = String(variant.inventory);
          inp.disabled = false;
        } else {
          invEl.textContent = '—';
          inp.disabled = false;
        }
      }
    });

    // Type
    e.typeBtns.forEach(btn => {
      const isNoneBtn = btn.dataset.type === 'None';
      const isActive = isNoneBtn ? state.current.type === '' : btn.dataset.type === state.current.type;
      btn.classList.toggle('active', isActive);
    });

    // Positions
    renderPositions();

    // Uploads
    renderUploads();
  }

  /* ------------------------------------------------------------------
     Color Selection
     ------------------------------------------------------------------ */
  function selectColor(colorName) {
    if (!state.current) resetCurrentGroup();
    state.current.color = colorName;
    state.current.sizes = {};
    syncUIFromCurrent();
    updateAddColorButton();
    updatePricingDisplay();

    // Switch media gallery to the color's variant image
    const colorInfo = (config.colors || []).find(c => c.name === colorName);
    if (colorInfo && colorInfo.mediaId && config.sectionId) {
      const gallery = document.querySelector(`[id^="MediaGallery-${config.sectionId}"]`);
      if (gallery && typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(`${config.sectionId}-${colorInfo.mediaId}`);
      }
    }
  }

  /* ------------------------------------------------------------------
     Size Inputs
     ------------------------------------------------------------------ */
  function onSizeChange(size, value) {
    if (!state.current) resetCurrentGroup();
    const qty = parseInt(value, 10) || 0;
    if (qty > 0) {
      state.current.sizes[size] = qty;
    } else {
      delete state.current.sizes[size];
    }
    updateAddColorButton();
    updatePricingDisplay();
  }

  function updateAddColorButton() {
    const e = getEls();
    if (!e || !e.addColorBtn) return;
    const hasColor = !!state.current?.color;
    const hasQty = groupTotalQty(state.current) > 0;
    e.addColorBtn.classList.toggle('gm-cob__hidden', !(hasColor && hasQty));
  }

  /* ------------------------------------------------------------------
     Add Color Group
     ------------------------------------------------------------------ */
  function addColorGroup() {
    const e = getEls();
    if (!e) return;

    // Validate
    if (!state.current.color) {
      showError(S.errorSelectColor || 'Please select a color first.');
      return;
    }
    const total = groupTotalQty(state.current);
    if (total === 0) {
      showError(S.errorEnterQuantity || 'Please enter a quantity for at least one size.');
      return;
    }

    clearError();
    state.groups.push({ ...state.current, files: Object.fromEntries(state.files) });
    resetCurrentGroup();
    renderSelectedColors();
    syncUIFromCurrent();
    updateAddColorButton();
    updatePricingDisplay();
  }

  /* ------------------------------------------------------------------
     Type Selection
     ------------------------------------------------------------------ */
  function selectType(typeName) {
    if (!state.current) resetCurrentGroup();
    if (typeName === 'None') {
      state.current.type = '';
    } else {
      state.current.type = state.current.type === typeName ? '' : typeName;
    }
    state.current.positions = [];
    state.files.clear();
    syncUIFromCurrent();
    updatePricingDisplay();
  }

  /* ------------------------------------------------------------------
     Positions
     ------------------------------------------------------------------ */
  function renderPositions() {
    const e = getEls();
    if (!e || !e.positionsWrap) return;

    const step = e.root.querySelector('[data-step="positions"]');
    if (!state.current?.type) {
      e.positionsWrap.classList.add('gm-cob__hidden');
      if (step) step.classList.add('gm-cob__hidden');
      return;
    }
    e.positionsWrap.classList.remove('gm-cob__hidden');
    if (step) step.classList.remove('gm-cob__hidden');

    const wrap = e.positionsWrap.querySelector('.gm-cob__positions');
    if (!wrap) return;

    wrap.innerHTML = '';
    
    // Get positions for the selected type
    const positionsByType = config.positionsByType || {};
    const typePositions = positionsByType[state.current.type] || [];
    
    typePositions.forEach(pos => {
      const price = getPositionPrice(state.current.type, pos);
      const isActive = state.current.positions.some(p => p.position === pos);
      const card = document.createElement('div');
      card.className = `gm-cob__position-card ${isActive ? 'active' : ''}`;
      card.dataset.position = pos;
      card.innerHTML = `
        <span class="gm-cob__position-checkbox">
          <svg viewBox="0 0 24 24" width="20" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </span>
        <span class="gm-cob__position-content">
          <span class="gm-cob__position-label">${pos}</span>
          <span class="gm-cob__position-price">${fmtMoney(price)}</span>
        </span>
      `;
      card.addEventListener('click', () => togglePosition(pos));
      wrap.appendChild(card);
    });

    updatePositionsSummary();
  }

  function togglePosition(pos) {
    if (!state.current) return;
    const idx = state.current.positions.findIndex(p => p.position === pos);
    const singleMode = config.allowMultiplePositions === 'single';
    if (idx >= 0) {
      state.current.positions.splice(idx, 1);
      state.files.delete(pos);
    } else {
      if (singleMode) {
        state.current.positions.forEach(p => state.files.delete(p.position));
        state.current.positions = [{ position: pos, artworkUrl: '' }];
      } else {
        state.current.positions.push({ position: pos, artworkUrl: '' });
      }
    }
    renderPositions();
    renderUploads();
    updatePricingDisplay();
  }

  function updatePositionsSummary() {
    const e = getEls();
    if (!e || !e.positionsSummary) return;
    const count = state.current?.positions?.length || 0;
    const total = getCustomizationPerUnit();
    if (count > 0) {
      e.positionsSummary.textContent = `Adds ${fmtMoney(total)} per item for ${count} location${count > 1 ? 's' : ''}`;
      e.positionsSummary.classList.remove('gm-cob__hidden');
    } else {
      e.positionsSummary.classList.add('gm-cob__hidden');
    }
  }

  /* ------------------------------------------------------------------
     Uploads
     ------------------------------------------------------------------ */
  function renderUploads() {
    const e = getEls();
    if (!e || !e.uploadsWrap) return;

    const step = e.root.querySelector('[data-step="uploads"]');
    const positions = state.current?.positions || [];
    if (!positions.length) {
      e.uploadsWrap.classList.add('gm-cob__hidden');
      if (step) step.classList.add('gm-cob__hidden');
      return;
    }
    e.uploadsWrap.classList.remove('gm-cob__hidden');
    if (step) step.classList.remove('gm-cob__hidden');

    const wrap = e.uploadsWrap.querySelector('.gm-cob__uploads');
    if (!wrap) return;

    // Re-use existing zones, only add missing ones
    const existing = new Map();
    wrap.querySelectorAll('.gm-cob__upload-item').forEach(el => {
      existing.set(el.dataset.position, el);
    });

    positions.forEach(p => {
      if (existing.has(p.position)) return;
      const item = document.createElement('div');
      item.className = 'gm-cob__upload-item gm-cob__anim-in';
      item.dataset.position = p.position;
      item.innerHTML = `
        <label class="gm-cob__upload-label">${(S.uploadLabelTemplate || '{position} Artwork').replace('{position}', p.position)}</label>
        <div class="gm-cob__upload-zone" data-position="${p.position}">
          <input type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,application/pdf" data-position="${p.position}">
          <div class="gm-cob__upload-text"><span>${S.uploadChooseFile || 'CHOOSE FILE'}</span> ${S.uploadDragDrop || 'or drag & drop'}</div>
          <div class="gm-cob__upload-preview gm-cob__hidden"></div>
        </div>
      `;
      wrap.appendChild(item);
    });

    // Remove zones for deselected positions
    existing.forEach((el, pos) => {
      if (!positions.some(p => p.position === pos)) el.remove();
    });

    bindUploadZones();
  }

  function bindUploadZones() {
    const e = getEls();
    if (!e) return;
    e.uploadZones().forEach(zone => {
      const input = zone.querySelector('input[type="file"]');
      if (input.dataset.bound) return;
      input.dataset.bound = '1';

      input.addEventListener('change', (ev) => {
        const file = ev.target.files[0];
        if (!file) return;
        const pos = zone.dataset.position;

        // Validate file type
        const allowedTypes = [
          'image/png',
          'image/jpeg',
          'image/jpg',
          'image/svg+xml',
          'application/pdf',
        ];
        const allowedExts = ['.png', '.jpg', '.jpeg', '.svg', '.pdf'];
        const isValidType = allowedTypes.includes(file.type);
        const isValidExt = allowedExts.some(ext => file.name.toLowerCase().endsWith(ext));
        if (!isValidType && !isValidExt) {
          showError(`Invalid file type. Only PNG, JPG, JPEG, SVG, and PDF are allowed.`);
          input.value = '';
          return;
        }

        // Read file as base64 data URL so it can be uploaded on Add to Cart
        const reader = new FileReader();
        reader.onload = () => { state.files.set(pos, reader.result); };
        reader.readAsDataURL(file);

        // Show preview
        const preview = zone.querySelector('.gm-cob__upload-preview');
        const text = zone.querySelector('.gm-cob__upload-text');
        const removeBtnHtml = `
          <button type="button" class="gm-cob__upload-remove" title="${S.fileRemoveTitle || 'Remove'}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        `;
        if (file.type.startsWith('image/')) {
          const url = URL.createObjectURL(file);
          preview.innerHTML = `<img src="${url}" alt="" loading="lazy" decoding="async">${removeBtnHtml}`;
        } else {
          preview.innerHTML = `<span style="font-size:1.2rem;font-weight:600;">${file.name}</span>${removeBtnHtml}`;
        }
        preview.classList.remove('gm-cob__hidden');
        text.classList.add('gm-cob__hidden');

        preview.querySelector('.gm-cob__upload-remove').addEventListener('click', (ev2) => {
          ev2.stopPropagation();
          state.files.delete(pos);
          input.value = '';
          preview.classList.add('gm-cob__hidden');
          text.classList.remove('gm-cob__hidden');
        });
      });

      // Drag & drop
      zone.addEventListener('dragover', (ev) => {
        ev.preventDefault();
        zone.classList.add('drag-over');
      });
      zone.addEventListener('dragleave', () => zone.classList.remove('drag-over'));
      zone.addEventListener('drop', (ev) => {
        ev.preventDefault();
        zone.classList.remove('drag-over');
        const file = ev.dataTransfer.files[0];
        if (file) {
          const dt = new DataTransfer();
          dt.items.add(file);
          input.files = dt.files;
          input.dispatchEvent(new Event('change'));
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Upload to Shopify Worker
     ------------------------------------------------------------------ */
  async function uploadFile(dataUrl, position, groupId) {
    const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!match) throw new Error('Invalid file data');
    const mimeType = match[1];
    const ext = mimeType.split('/')[1] || 'png';
    const safePos = position.replace(/\s+/g, '-').toLowerCase();
    const ts = Date.now();
    const filename = `artwork-${safePos}-${groupId}-${ts}.${ext}`;

    const res = await fetch(`${WORKER_URL}/upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        image: dataUrl,
        filename,
        mimeType,
        groupId: groupId || null,
        designId: `${config.productId}-${position}`,
      }),
    });
    const json = await res.json();
    if (!json.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json.fileUrl;
  }

  /* ------------------------------------------------------------------
     Overlay Controller
     ------------------------------------------------------------------ */
  function showOverlay() {
    const e = getEls();
    if (!e || !e.overlay) return;
    e.overlay.classList.add('active');
    e.overlay.setAttribute('aria-hidden', 'false');

    // Reset to processing card
    if (e.overlayProcessing) e.overlayProcessing.classList.add('visible');
    if (e.overlayResult) e.overlayResult.classList.remove('visible');

    // Reset result state
    if (e.overlaySuccessIcon) e.overlaySuccessIcon.classList.remove('visible');
    if (e.overlayErrorIcon) e.overlayErrorIcon.classList.remove('visible');
    if (e.overlayTitle) e.overlayTitle.textContent = '';
    if (e.overlayContext) e.overlayContext.textContent = '';
    if (e.overlaySub) e.overlaySub.textContent = '';
    if (e.overlayActions) e.overlayActions.innerHTML = '';

    // Reset processing state
    if (e.overlayStep) e.overlayStep.textContent = '3';
    if (e.overlayStatus) e.overlayStatus.textContent = S.overlayStepPrepare || 'Preparing your order';
    if (e.overlayBar) e.overlayBar.style.width = '0%';
    if (e.overlayDetail) e.overlayDetail.textContent = '';
  }

  function hideOverlay(delay = 0) {
    const e = getEls();
    if (!e || !e.overlay) return;
    setTimeout(() => {
      e.overlay.classList.remove('active');
      e.overlay.setAttribute('aria-hidden', 'true');
      if (e.overlayProcessing) e.overlayProcessing.classList.remove('visible');
      if (e.overlayResult) e.overlayResult.classList.remove('visible');
    }, delay);
  }

  function setOverlayStep(stepNum, status, detail = '', progressPercent = 0) {
    const e = getEls();
    if (!e || !e.overlayStep) return;
    e.overlayStep.textContent = String(stepNum);
    e.overlayStep.classList.remove('pulse');
    void e.overlayStep.offsetWidth; // reflow
    e.overlayStep.classList.add('pulse');
    if (e.overlayStatus) e.overlayStatus.textContent = status;
    if (e.overlayDetail) e.overlayDetail.textContent = detail;
    if (e.overlayBar) e.overlayBar.style.width = `${progressPercent}%`;
  }

  function switchToResultCard() {
    const e = getEls();
    if (!e) return;
    if (e.overlayProcessing) e.overlayProcessing.classList.remove('visible');
    if (e.overlayResult) e.overlayResult.classList.add('visible');
  }

  function showOverlaySuccess(title, sub, actions = []) {
    const e = getEls();
    if (!e || !e.overlay) return;
    switchToResultCard();
    if (e.overlayErrorIcon) e.overlayErrorIcon.classList.remove('visible');
    if (e.overlaySuccessIcon) e.overlaySuccessIcon.classList.add('visible');
    if (e.overlayTitle) e.overlayTitle.textContent = title;
    if (e.overlayContext) e.overlayContext.textContent = '';
    if (e.overlaySub) e.overlaySub.textContent = sub;
    renderOverlayActions(actions);
  }

  function showOverlayError(title, sub, context = '', actions = []) {
    const e = getEls();
    if (!e || !e.overlay) return;
    switchToResultCard();
    if (e.overlaySuccessIcon) e.overlaySuccessIcon.classList.remove('visible');
    if (e.overlayErrorIcon) e.overlayErrorIcon.classList.add('visible');
    if (e.overlayTitle) e.overlayTitle.textContent = title;
    if (e.overlayContext) e.overlayContext.textContent = context;
    if (e.overlaySub) e.overlaySub.textContent = sub;
    renderOverlayActions(actions);
  }

  function renderOverlayActions(actions) {
    const e = getEls();
    if (!e || !e.overlayActions) return;
    e.overlayActions.innerHTML = '';
    actions.forEach((a, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `gm-cob__overlay-btn gm-cob__overlay-btn--${a.variant || 'primary'}`;
      btn.textContent = a.label;
      if (a.onClick) btn.addEventListener('click', () => a.onClick(btn));
      e.overlayActions.appendChild(btn);
    });
  }

  /* ------------------------------------------------------------------
     Validation
     ------------------------------------------------------------------ */
  function showError(msg) {
    const e = getEls();
    if (!e || !e.errors) return;
    e.errors.textContent = msg;
    e.errors.classList.add('visible');
  }

  function clearError() {
    const e = getEls();
    if (!e || !e.errors) return;
    e.errors.classList.remove('visible');
    e.errors.textContent = '';
  }

  /* ------------------------------------------------------------------
     Cart Submission
     ------------------------------------------------------------------ */
  async function submitToCart() {
    const e = getEls();
    if (!e) return;

    // If current group has data, auto-add it first
    const currentTotal = state.current?.color ? groupTotalQty(state.current) : 0;
    if (currentTotal > 0) {
      const valid = validateCurrent();
      if (!valid) return;
      state.groups.push({ ...state.current, files: Object.fromEntries(state.files) });
      resetCurrentGroup();
      renderSelectedColors();
      syncUIFromCurrent();
    }

    if (state.groups.length === 0) {
      showError(S.errorNoColorGroups || 'Please add at least one color group with quantities.');
      return;
    }

    const totalQty = getTotalQty();
    const tier = getDiscountTier(totalQty);
    const discountPct = tier ? (tier.discount_percentage || 0) : 0;

    const items = [];
    const orderGroupId = `gm-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const globalType = state.current?.type || '';
    const globalPositions = state.current?.positions || [];
    const globalCusPerUnit = getCustomizationPerUnit();

    // Pre-validate all customization variants exist
    const missingVariants = [];
    const multiMode = config.allowMultiplePositions === 'multiple';
    if (globalType && globalPositions.length) {
      if (multiMode && globalPositions.length > 1) {
        const combined = findCombinedCustomizationVariant(globalType, globalPositions);
        if (!combined) {
          missingVariants.push(`${globalType} — ${globalPositions.map(p => p.position).join(' + ')}`);
        }
      } else {
        globalPositions.forEach(p => {
          const cusVariant = findCustomizationVariant(globalType, p.position);
          if (!cusVariant) {
            missingVariants.push(`${globalType} — ${p.position}`);
          }
        });
      }
    }
    if (missingVariants.length > 0) {
      const uniqueMissing = [...new Set(missingVariants)];
      const missingTpl = S.errorMissingCustomization || 'Missing customization: {list}. Please check your product setup.';
      showError(missingTpl.replace('{list}', uniqueMissing.join(', ')));
      return;
    }

    clearError();
    e.cartBtn.disabled = true;
    showOverlay();

    // Step 3: Preparing
    setOverlayStep(3, S.overlayStepPrepare || 'Preparing your order', '', 10);
    await new Promise(r => setTimeout(r, 400));

    // Step 2: Upload artwork files to Shopify via Cloudflare Worker
    const uploadErrors = [];
    const filesToUpload = [];
    for (const g of state.groups) {
      const files = g.files || {};
      for (const [pos, dataUrl] of Object.entries(files)) {
        if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
          filesToUpload.push({ g, pos, dataUrl });
        }
      }
    }

    if (filesToUpload.length > 0) {
      setOverlayStep(2, S.overlayStepUpload || 'Uploading artwork', `${filesToUpload.length} file${filesToUpload.length !== 1 ? 's' : ''}`, 20);
      let uploaded = 0;
      for (const { g, pos, dataUrl } of filesToUpload) {
        try {
          const cdnUrl = await uploadFile(dataUrl, pos, orderGroupId);
          g.files[pos] = cdnUrl;
          uploaded++;
          const pct = 20 + Math.round((uploaded / filesToUpload.length) * 40);
          setOverlayStep(2, S.overlayStepUpload || 'Uploading artwork', `${uploaded} of ${filesToUpload.length}`, pct);
        } catch (err) {
          uploadErrors.push(`${pos}: ${err.message}`);
        }
      }
    }

    if (uploadErrors.length > 0) {
      const uploadErrTpl = S.errorUploadFailed || 'Upload failed: {errors}';
      showOverlayError(
        S.overlayErrorTitle || 'Upload failed',
        uploadErrTpl.replace('{errors}', uploadErrors.join('; ')),
        'Stage: File Upload',
        [
          { label: 'Try Again', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          { label: 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
        ]
      );
      return;
    }

    // Step 1: Build items and add to cart
    setOverlayStep(1, S.overlayStepCart || 'Adding to cart', '', 65);
    await new Promise(r => setTimeout(r, 300));

    state.groups.forEach(g => {
      for (const [size, qtyStr] of Object.entries(g.sizes || {})) {
        const qty = parseInt(qtyStr, 10) || 0;
        if (qty <= 0) continue;
        const variant = findVariant(g.color, size);
        if (!variant) continue;

        items.push({
          id: variant.id,
          quantity: qty,
        });
      }

      // Add customization product line item(s)
      const groupTotal = groupTotalQty(g);
      if (globalType && globalPositions.length && groupTotal > 0) {
        const groupFiles = g.files || {};
        const fileProps = {};
        for (const [pos, url] of Object.entries(groupFiles)) {
          if (typeof url === 'string' && url.startsWith('http')) {
            fileProps[`gm_artwork_${pos}`] = url;
          }
        }

        if (multiMode && globalPositions.length > 1) {
          const combinedVariant = findCombinedCustomizationVariant(globalType, globalPositions);
          if (combinedVariant) {
            items.push({
              id: combinedVariant.id,
              quantity: groupTotal,
              properties: {
                '_gm_linked_product': config.productId,
                '_gm_color_group_id': orderGroupId,
                ...fileProps,
              },
            });
          } else {
            globalPositions.forEach(p => {
              const cusVariant = findCustomizationVariant(globalType, p.position);
              if (!cusVariant) return;
              items.push({
                id: cusVariant.id,
                quantity: groupTotal,
                properties: {
                  '_gm_linked_product': config.productId,
                  '_gm_color_group_id': orderGroupId,
                  ...fileProps,
                },
              });
            });
          }
        } else {
          globalPositions.forEach(p => {
            const cusVariant = findCustomizationVariant(globalType, p.position);
            if (!cusVariant) return;
            items.push({
              id: cusVariant.id,
              quantity: groupTotal,
              properties: {
                '_gm_linked_product': config.productId,
                '_gm_color_group_id': orderGroupId,
                ...fileProps,
              },
            });
          });
        }
      }
    });

    if (items.length === 0) {
      showOverlayError(
        S.overlayErrorTitle || 'No valid items',
        S.errorNoValidItems || 'No valid items to add. Please check your selections.',
        'Stage: Build Cart Items',
        [
          { label: 'Review Selections', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          { label: 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
        ]
      );
      return;
    }

    try {
      setOverlayStep(1, S.overlayStepCart || 'Adding to cart', `${items.length} item${items.length !== 1 ? 's' : ''}`, 80);

      const drawer = document.querySelector('cart-drawer');
      const sectionIds = drawer ? drawer.getSectionsToRender().map(s => s.id) : ['cart-drawer', 'cart-icon-bubble'];

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();

      if (data.status || data.description) {
        showOverlayError(
          S.overlayErrorTitle || 'Something went wrong',
          data.description || data.message || 'Please try again.',
          `Stage: Add to Cart (HTTP ${res.status})`,
          [
            { label: 'Try Again', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
            { label: 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          ]
        );
        return;
      }

      if (data.items && data.items.length) {
        // Clear groups after successful add
        state.groups = [];
        resetCurrentGroup();
        renderSelectedColors();
        syncUIFromCurrent();
        updateAddColorButton();
        updatePricingDisplay();

        // Trigger theme-wide cart update (subtle refresh in background)
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gm-custom-pricing', cartData: data });
        }

        // Success with CTA buttons — user decides what to do next
        const itemCount = data.items.reduce((sum, it) => sum + (it.quantity || 0), 0);
        showOverlaySuccess(
          S.overlaySuccessTitle || 'Added successfully!',
          S.overlaySuccessSub || `${itemCount} item${itemCount !== 1 ? 's' : ''} added to your cart.`,
          [
            {
              label: 'View Cart',
              variant: 'primary',
              onClick: () => {
                hideOverlay();
                // Open cart drawer and render if possible
                if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
                  drawer.classList.remove('is-empty');
                  drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
                  drawer.renderContents(data);
                } else if (drawer && drawer.open) {
                  drawer.open();
                } else {
                  window.location.href = '/cart';
                }
              },
            },
            {
              label: 'Continue Shopping',
              variant: 'secondary',
              onClick: () => { hideOverlay(); },
            },
          ]
        );
      } else {
        showOverlayError(
          S.overlayErrorTitle || 'Something went wrong',
          S.overlayErrorSub || 'The cart response was empty. Please try again or contact support.',
          'Stage: Cart Response Validation',
          [
            { label: 'Try Again', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
            { label: 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          ]
        );
      }
    } catch (err) {
      showOverlayError(
        S.overlayErrorTitle || 'Something went wrong',
        S.overlayErrorSub || 'Please try again or contact support.',
        `Stage: Network / ${err.name || 'Error'}`,
        [
          { label: 'Try Again', variant: 'primary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
          { label: 'Close', variant: 'secondary', onClick: () => { hideOverlay(); e.cartBtn.disabled = false; } },
        ]
      );
    } finally {
      e.cartBtn.disabled = false;
    }
  }

  function validateCurrent() {
    if (!state.current?.color) {
      showError('Please select a color.');
      return false;
    }
    if (groupTotalQty(state.current) === 0) {
      showError('Please enter a quantity for at least one size.');
      return false;
    }
    clearError();
    return true;
  }

  /* ------------------------------------------------------------------
     Event Binding
     ------------------------------------------------------------------ */
  function init() {
    const e = getEls();
    if (!e) return;

    resetCurrentGroup();

    // Default: select "None" type
    state.current.type = '';
    syncUIFromCurrent();

    // Auto-select first color
    const firstColor = config.colors?.[0]?.name;
    if (firstColor) {
      selectColor(firstColor);
    }

    // Color buttons
    e.colorBtns.forEach(btn => {
      btn.addEventListener('click', () => selectColor(btn.dataset.color));
    });

    // Size inputs
    e.sizeInputs.forEach(inp => {
      inp.addEventListener('input', () => onSizeChange(inp.dataset.size, inp.value));
      inp.addEventListener('change', () => onSizeChange(inp.dataset.size, inp.value));
    });

    // Add color button
    if (e.addColorBtn) {
      e.addColorBtn.addEventListener('click', addColorGroup);
    }

    // Type buttons
    e.typeBtns.forEach(btn => {
      btn.addEventListener('click', () => selectType(btn.dataset.type));
    });

    // Cart button
    if (e.cartBtn) {
      e.cartBtn.addEventListener('click', submitToCart);
    }

    updateAddColorButton();
    updatePricingDisplay();
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
